import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Web Speech API wrapper for live shop-floor dictation.
 *
 * Browser support:
 *   - Chrome (desktop + Android): excellent quality.
 *   - Safari (iOS 14.5+ / macOS 14+): supported as `webkitSpeechRecognition`.
 *   - Firefox: not supported (yet — falls back to error state).
 *   - Edge: supported.
 *
 * Why browser-side instead of uploading audio to a transcription
 * service: zero backend cost, zero network round-trip per syllable,
 * works offline-ish (browser hits Google / Apple), and the mechanic
 * sees the words appear as they speak — fast feedback when they were
 * mid-thought and want to start again. The tradeoff is browser
 * support gaps; if Firefox usage grows we'd swap to Cloudflare AI
 * Whisper with a server-side upload.
 *
 * Two phases per utterance:
 *   - `interim`: live partial transcript while speaking. Replaces on
 *     every recogniser tick.
 *   - `final`: confirmed segment after a pause. Appends to the running
 *     finalised transcript and clears interim.
 *
 * The hook exposes the running concatenated transcript (final + interim
 * with a separator) plus a control surface: start, stop, reset.
 */

// Minimal local type shim — TS doesn't ship Web Speech API types and
// the project doesn't pull `@types/dom-speech-recognition`. Keeps the
// dependency-free posture intact.
type SpeechRecognitionResultLike = {
  isFinal: boolean
  0: { transcript: string }
}
type SpeechRecognitionEventLike = {
  resultIndex: number
  results: ArrayLike<SpeechRecognitionResultLike>
}
type SpeechRecognitionErrorEventLike = {
  error: string
  message?: string
}
type SpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((e: SpeechRecognitionEventLike) => void) | null
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getSpeechRecognition(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition) ?? null
}

export type SpeechRecognitionStatus =
  | 'idle'
  | 'listening'
  | 'unsupported'
  | 'error'
  | 'denied'

// Defense-in-depth cap on a single listen. The push-to-talk surfaces stop on
// release via the window press tail (usePressTail), but if a release is ever
// lost entirely — no pointer event at all, the one ghost case JS can't
// observe — a continuous recogniser would stay hot until navigation. 60s
// bounds that; the cap calls stop(), which commits the final segment exactly
// like a normal release, so nothing said is lost and a fresh press resumes.
export const MAX_LISTEN_MS = 60_000

export function useSpeechRecognition(options?: {
  lang?: string
}) {
  const lang = options?.lang ?? 'en-CA'
  // Probe the browser API once via lazy init so the UI can render the
  // "unsupported" hint without ever calling start() against a missing
  // recogniser. Was previously a mount-effect.
  const [status, setStatus] = useState<SpeechRecognitionStatus>(() =>
    getSpeechRecognition() ? 'idle' : 'unsupported',
  )
  const [transcript, setTranscript] = useState('')
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  // Stash the latest finalised text in a ref so the recogniser callback
  // (which captures stale state across React renders) can append
  // without losing words said across a quick stop / start.
  //
  // The cumulative-final transcript is split in two:
  //   - `finalRef`: confirmed-distinct segments concatenated together
  //   - `lastSegmentRef`: the most recent finalised segment, still
  //     eligible to be replaced by a longer snapshot from Android
  //     Chrome (see the snapshot-dedup note in onresult below).
  // Displayed transcript = `${finalRef} ${lastSegmentRef}`.trim().
  const finalRef = useRef('')
  const lastSegmentRef = useRef('')
  // The MAX_LISTEN_MS timer for the current listen (armed in start, cleared
  // on stop / onend / unmount).
  const capRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCap = useCallback(() => {
    if (capRef.current !== null) {
      clearTimeout(capRef.current)
      capRef.current = null
    }
  }, [])

  const stop = useCallback(() => {
    clearCap()
    const rec = recognitionRef.current
    if (rec) {
      try {
        rec.stop()
      } catch {
        // stop() on an already-stopped recogniser throws InvalidStateError.
      }
    }
  }, [clearCap])

  const start = useCallback(() => {
    const Ctor = getSpeechRecognition()
    if (!Ctor) {
      setStatus('unsupported')
      return
    }
    // Re-instantiate every start. Safari leaks state across restarts
    // (interim never clears on the second run); a fresh recogniser
    // sidesteps it and Chrome doesn't mind.
    const rec = new Ctor()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = lang
    rec.onresult = (e) => {
      // Snapshot-dedup. The W3C contract says each `isFinal` result is
      // a distinct, non-overlapping segment, but Android Chrome (with
      // continuous + interimResults) instead emits multiple final
      // results that each contain a progressively longer snapshot of
      // the SAME utterance — "oil" → "oil" → "oil capacity" → "oil
      // capacity 3.9" → "oil capacity 3.9 liters". Treating every
      // final as additive concatenated all of those into one duplicated
      // string (see Notion ticket — Emil's phone, vehicle Notes voice
      // input). Instead, hold the most recent final in `lastSegmentRef`
      // and let the next final REPLACE it when the new text starts
      // with the old — that's the snapshot relationship. Genuinely
      // distinct segments (after a pause) don't share a prefix, so
      // they commit `lastSegment` into `finalRef` and become the new
      // `lastSegment`. Behaviour on platforms that already emit
      // non-overlapping finals (desktop Chrome, iOS Safari) is
      // unchanged — they hit the "commit prior" branch every time.
      let live = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        if (r.isFinal) {
          const seg = r[0].transcript.trim()
          if (!seg) continue
          const last = lastSegmentRef.current
          if (last && seg.startsWith(last)) {
            // Snapshot extension — replace.
            lastSegmentRef.current = seg
          } else if (last && last.startsWith(seg)) {
            // Defensive: recogniser briefly emitted a shorter snapshot
            // of what we already hold. Keep the longer existing one.
          } else {
            // Distinct segment — commit prior `lastSegment` (if any)
            // into the running final transcript, then this becomes
            // the new `lastSegment`.
            if (last) {
              finalRef.current = (finalRef.current + ' ' + last).trim()
            }
            lastSegmentRef.current = seg
          }
        } else {
          live += r[0].transcript
        }
      }
      const display = (finalRef.current + ' ' + lastSegmentRef.current).trim()
      setTranscript(display)
      setInterim(live)
    }
    rec.onerror = (e) => {
      // 'no-speech' is normal — the recogniser timed out without input.
      // Stay in listening state; the UI knows nothing to show.
      if (e.error === 'no-speech') return
      // 'aborted' is what we trigger on the user's "Stop" press.
      if (e.error === 'aborted') return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setStatus('denied')
        setError('Microphone access denied. Allow it in your browser settings.')
        return
      }
      setStatus('error')
      setError(e.message ?? e.error ?? 'Speech recognition failed.')
    }
    rec.onend = () => {
      // Guard to the LIVE instance: browsers fire onend asynchronously
      // (50-500ms after stop(), once audio flushes to the service), so on a
      // quick re-press the OLD instance's late onend lands after the new
      // listen started — without the guard it would disarm the new listen's
      // cap and flip its status to idle (which prematurely fires
      // PushToTalkMic's emit-on-settle). Reviewers reproduced both.
      if (recognitionRef.current !== rec) return
      // The recogniser can end itself (no-speech timeout, service drop) —
      // the cap must not outlive the listen it was armed for.
      clearCap()
      setStatus((prev) => (prev === 'listening' ? 'idle' : prev))
      setInterim('')
    }
    recognitionRef.current = rec
    setError(null)
    setStatus('listening')
    try {
      rec.start()
    } catch (err) {
      // start() on an already-started recogniser throws — treat as no-op.
      console.warn('SpeechRecognition.start failed', err)
    }
    clearCap()
    capRef.current = setTimeout(stop, MAX_LISTEN_MS)
  }, [lang, clearCap, stop])

  const reset = useCallback(() => {
    stop()
    finalRef.current = ''
    lastSegmentRef.current = ''
    setTranscript('')
    setInterim('')
    setError(null)
  }, [stop])

  // Stop the recogniser on unmount so we don't leak a mic stream (and the
  // cap timer with it).
  useEffect(() => {
    return () => {
      clearCap()
      recognitionRef.current?.abort?.()
    }
  }, [clearCap])

  return {
    status,
    transcript,
    interim,
    error,
    start,
    stop,
    reset,
    supported: status !== 'unsupported',
    combined: transcript + (interim ? ' ' + interim : ''),
  }
}
